import type { HTMLMotionProps } from 'motion/react'
import { FileSearch } from 'lucide-react'
import { motion } from 'motion/react'

import cn from '@config/tailwind/cn'

/** Minimal book shape needed to render a cover tile. Both library and my-book-collection queries satisfy this. */
interface GalleryBook {
  book: { id: string; coverUrl: string }
}

interface BookGalleryCardProps extends Omit<HTMLMotionProps<'button'>, '$typeof' | 'className'> {
  collection: GalleryBook[]
  className?: string
  cardClassName?: string
  itemClassName?: string
  imageSize?: number
  children?: React.ReactNode
  withAnimation?: boolean
}

/** Max book covers shown in the gallery grid. */
const MAX_COVERS = 4

export function BookGalleryCard({
  collection,
  imageSize = 200,
  children,
  cardClassName,
  className,
  itemClassName,
  withAnimation = true,
  ...props
}: BookGalleryCardProps) {
  const covers = collection.slice(0, MAX_COVERS)

  return (
    <motion.button
      whileHover={withAnimation ? { scale: 1.05 } : undefined}
      whileTap={withAnimation ? { scale: 0.95 } : undefined}
      className={cn('srd:w-full srd:h-fit srd:rounded-lg srd:shrink-0', className)}
      {...props}
    >
      <div
        className={cn(
          'srd:w-full srd:h-full srd:aspect-square srd:border srd:border-grey-20 srd:rounded-lg srd:overflow-clip',
          covers.length === 0 && 'srd:bg-blue-20',
          cardClassName,
        )}
      >
        {covers.length > 0 ? (
          <div
            className={cn(
              'srd:w-full srd:h-full srd:aspect-square srd:grid srd:grid-cols-2 srd:grid-rows-2 srd:bg-blue-20',
              itemClassName,
            )}
          >
            {covers.map((book) => (
              <img
                key={book.book.id}
                src={book.book.coverUrl}
                alt=''
                width={imageSize / 2}
                height={imageSize / 2}
                draggable={false}
                className='srd:w-full srd:h-full srd:object-cover'
              />
            ))}
          </div>
        ) : (
          <div
            className={cn(
              'srd:w-full srd:h-full srd:aspect-square srd:flex srd:items-center srd:justify-center srd:bg-blue-20',
              itemClassName,
            )}
          >
            <FileSearch className='srd:w-10 srd:h-10 srd:text-blue-100' />
          </div>
        )}
      </div>

      {children}
    </motion.button>
  )
}
